/**
 * DGRiskConfigEditor.tsx — SME editor for the two risk blocks that drive the
 * confidence scores and the classification rulebook. Fully config-driven: it
 * edits confidenceModel + classificationRules on the DGAppConfig snapshot, which
 * persists to <app>_<entity>_dg_setup_config.json and is honoured by
 * merge_dg_config on the backend. No values are hardcoded in the review UI.
 *
 * Mirrors the existing ScoringModelEditor / CDEScoringEditor pattern in
 * DGConfigModal (controlled component: value in, onChange out).
 */
import React from "react";

const L = { muted: "#64748b", label: "#2c3e52", brand: "#1e3a6e", border: "#d7dee8", card: "#fff", bg: "#fafbfd", ink: "#0f1923" };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: L.muted, textTransform: "uppercase", letterSpacing: "0.06em" };
const num: React.CSSProperties = { width: 64, padding: "4px 6px", fontSize: 12, border: `1px solid ${L.border}`, borderRadius: 6, textAlign: "right" };
const txt: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "5px 8px", fontSize: 12, border: `1px solid ${L.border}`, borderRadius: 6 };

function Section({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div style={{ border: `1px solid ${L.border}`, borderRadius: 10, background: L.card, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ ...lbl, color: L.brand, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

export const ConfidenceModelEditor: React.FC<{ model: any; onChange: (m: any) => void }> = ({ model, onChange }) => {
  if (!model) return null;
  const interp = model.interpretation ?? {};
  const setInterp = (k: string, v: number) => onChange({ ...model, interpretation: { ...interp, [k]: v } });
  const setAttrW = (i: number, v: number) => {
    const attributes = (model.attributes ?? []).map((a: any, j: number) => j === i ? { ...a, weight: v } : a);
    onChange({ ...model, attributes });
  };
  const wSum = (model.attributes ?? []).reduce((s: number, a: any) => s + (Number(a.weight) || 0), 0);

  return (
    <Section title="Classification Confidence — thresholds & weights">
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 14 }}>
        {[["high_min", "High ≥"], ["medium_min", "Medium ≥"], ["review_below", "Review <"]].map(([k, t]) => (
          <label key={k} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 12, color: L.label }}>{t}</span>
            <input type="number" step="0.05" min={0} max={1} style={num}
              value={interp[k] ?? ""} onChange={e => setInterp(k, parseFloat(e.target.value))} />
          </label>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 12, color: L.label }}>Rule-driven</span>
          <input type="number" step="0.05" min={0} max={1} style={num}
            value={model.rule_driven_confidence ?? ""} onChange={e => onChange({ ...model, rule_driven_confidence: parseFloat(e.target.value) })} />
        </label>
      </div>
      <div style={{ ...lbl, marginBottom: 6 }}>Roll-up weights {Math.abs(wSum - 1) > 0.001 && <span style={{ color: "#b45309", fontWeight: 700 }}>· sum {wSum.toFixed(2)} (renormalised at runtime)</span>}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px 12px" }}>
        {(model.attributes ?? []).map((a: any, i: number) => (
          <React.Fragment key={a.conf_key ?? i}>
            <span style={{ fontSize: 12, color: L.ink, alignSelf: "center" }}>{a.label ?? a.conf_key}</span>
            <input type="number" step="0.01" min={0} max={1} style={num} value={a.weight ?? ""} onChange={e => setAttrW(i, parseFloat(e.target.value))} />
          </React.Fragment>
        ))}
      </div>
    </Section>
  );
};

export const ClassificationRulesEditor: React.FC<{ rules: any; onChange: (r: any) => void }> = ({ rules, onChange }) => {
  if (!rules) return null;
  const setRuleValue = (si: number, ri: number, value: string) => {
    const sections = rules.sections.map((s: any, i: number) =>
      i !== si ? s : { ...s, rules: s.rules.map((r: any, j: number) => j === ri ? { ...r, value } : r) });
    onChange({ ...rules, sections });
  };
  const setRuleWhen = (si: number, ri: number, when: string) => {
    const sections = rules.sections.map((s: any, i: number) =>
      i !== si ? s : { ...s, rules: s.rules.map((r: any, j: number) => j === ri ? { ...r, when } : r) });
    onChange({ ...rules, sections });
  };

  return (
    <Section title="Classification Rulebook (shown to SMEs, in-app & export)">
      <textarea style={{ ...txt, minHeight: 50, marginBottom: 12 }} value={rules.intro ?? ""} onChange={e => onChange({ ...rules, intro: e.target.value })} />
      {(rules.sections ?? []).map((sec: any, si: number) => (
        <div key={sec.id ?? si} style={{ marginBottom: 14 }}>
          <div style={{ ...lbl, color: L.brand, marginBottom: 6 }}>{sec.title}</div>
          {(sec.rules ?? []).map((r: any, ri: number) => (
            <div key={ri} style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 8, marginBottom: 6 }}>
              <input style={txt} value={r.when ?? ""} onChange={e => setRuleWhen(si, ri, e.target.value)} />
              <input style={{ ...txt, fontWeight: 700 }} value={r.value ?? ""} onChange={e => setRuleValue(si, ri, e.target.value)} />
            </div>
          ))}
        </div>
      ))}
      {rules.unknowns && (
        <div>
          <div style={{ ...lbl, color: L.brand, marginBottom: 6 }}>{rules.unknowns.title ?? "Unknown values"}</div>
          <textarea style={{ ...txt, minHeight: 50 }} value={rules.unknowns.body ?? ""}
            onChange={e => onChange({ ...rules, unknowns: { ...rules.unknowns, body: e.target.value } })} />
        </div>
      )}
    </Section>
  );
};

/** Convenience wrapper DGConfigModal can mount in one line. */
const DGRiskConfigEditor: React.FC<{
  confidenceModel: any; classificationRules: any;
  onConfidenceChange: (m: any) => void; onRulesChange: (r: any) => void;
}> = ({ confidenceModel, classificationRules, onConfidenceChange, onRulesChange }) => (
  <div style={{ background: L.bg, padding: 2 }}>
    <ConfidenceModelEditor model={confidenceModel} onChange={onConfidenceChange} />
    <ClassificationRulesEditor rules={classificationRules} onChange={onRulesChange} />
  </div>
);
export default DGRiskConfigEditor;
